import { ProcessedTicketData, TicketElement } from './useTicketBuilder';
import { getDefaultTicketStyling, DEFAULT_TICKET_FONT_SIZE, Styling } from './DefaultStyling';
import { StorageData } from '~Components/Storage';
import { i18nProps, LocalItem } from '~Types';
import { PrintRow } from '~Components/PrinterManager';

// Helper functions for clean ticket generation matching Layout.tsx behavior
const createLineBreak = (): PrintRow => ({
  content: ' ',
  fontSize: 14,
});

const createDivider = (): PrintRow[] => [
  { content: ' ', fontSize: 14, alignment: 'LEFT' },
  { content: '───────────────────────────────────', alignment: 'LEFT' },
  { content: ' ', fontSize: 14, alignment: 'LEFT' },
];

const createSpacing = (height = 16): PrintRow[] => {
  const numBreaks = Math.max(1, Math.floor(height / 16));
  return Array(numBreaks)
    .fill(null)
    .map(() => createLineBreak());
};

// Generate print data using TicketBuilder system with proper styling
export const generateTicketPrintData = (
  ticketData: ProcessedTicketData,
  formatCurrency: (amount: number) => string,
  storage: StorageData,
  i18n: i18nProps,
  isAppOrder = false,
): PrintRow[] => {
  const printRows: PrintRow[] = [];

  // Use styling from ticketData if available (from template), otherwise fall back to default
  const ticketStyling =
    ticketData.styling ||
    getDefaultTicketStyling({
      storage,
      i18n,
      isTextPrinting: true,
    });

  // Process each element using the TicketBuilder structure
  ticketData.elements.forEach((element) => {
    const elementRows = processElement(
      element,
      ticketData,
      formatCurrency,
      storage,
      i18n,
      ticketStyling,
      isAppOrder,
    );
    printRows.push(...elementRows);
  });

  return printRows.filter((row): row is PrintRow => row !== null && row !== undefined);
};

const processElement = (
  element: TicketElement,
  ticketData: ProcessedTicketData,
  formatCurrency: (amount: number) => string,
  storage: StorageData,
  i18n: i18nProps,
  styling: Record<string, Omit<Styling, 'type'>>,
  isAppOrder = false,
): PrintRow[] => {
  const elementStyling = styling[element.type] || {};

  switch (element.type) {
    case 'SPACE':
      return createSpacing(element.height);

    case 'DIVIDER':
      return createDivider();

    case 'MAIN_CONTENT':
      return processMainContent(
        ticketData.groupsData,
        formatCurrency,
        storage,
        i18n,
        styling,
        isAppOrder,
      );

    case 'ORDER_COMMENT':
      if (element.data) {
        return [
          ...createDivider(),
          ...[createPrintRow(element.data, elementStyling)],
          ...createDivider(),
        ];
      } else {
        return [];
      }

    case 'STATION_TITLE':
      if (element.data) {
        return [...createSpacing(), ...[createPrintRow(element.data, elementStyling)]];
      } else {
        return [];
      }

    default:
      return [createPrintRow(element.data || '', elementStyling)];
  }
};

const createPrintRow = (content: string, styling: any): PrintRow => {
  const fontSize = styling.fontSize || DEFAULT_TICKET_FONT_SIZE;

  return {
    content,
    fontSize,
    bold: styling.fontWeight === 'bold',
    alignment: mapAlignment(styling.textAlign),
    doubleHeigth: fontSize >= 30,
    doubleWidth: fontSize >= 30,
  };
};

const mapAlignment = (textAlign?: string): 'LEFT' | 'CENTER' | 'RIGHT' => {
  switch (textAlign) {
    case 'center':
      return 'CENTER';
    case 'right':
      return 'RIGHT';
    default:
      return 'LEFT';
  }
};

const processMainContent = (
  groupsData: ProcessedTicketData['groupsData'],
  formatCurrency: (amount: number) => string,
  storage: StorageData,
  i18n: i18nProps,
  styling: Record<string, Omit<Styling, 'type'>>,
  isAppOrder = false,
): PrintRow[] => {
  const rows: PrintRow[] = [];

  groupsData.forEach((group, groupIndex) => {
    // Group title
    if (group.title) {
      rows.push(createPrintRow(group.title, styling.GROUP_TITLE || {}));
    }

    // Process main items
    group.items.forEach((item, index) => {
      rows.push(
        ...processItem({
          item,
          isFromOtherStation: false,
          formatCurrency,
          storage,
          i18n,
          styling,
          isLastItemInGroup: index === group.items.length - 1,
          isAppOrder,
        }),
      );
    });

    // Process items from other stations
    if (group.itemsFromOtherStations.length > 0) {
      rows.push(...createSpacing());
      group.itemsFromOtherStations.forEach((item) => {
        rows.push(
          ...processItem({
            item,
            isFromOtherStation: true,
            formatCurrency,
            storage,
            i18n,
            styling,
            isAppOrder,
          }),
        );
      });
    }

    // Add divider between groups (except for last group) - matching Layout.tsx behavior
    if (groupIndex < groupsData.length - 1) {
      if (styling.GROUP?.divider) {
        rows.push(...createDivider());
      } else {
        // Add spacing between groups if no divider
        const numBreaks = Math.max(1, Math.floor((styling.GROUP?.spaceBetweenGroups || 30) / 16));
        for (let i = 0; i < numBreaks; i++) {
          rows.push(createLineBreak());
        }
      }
    }
  });

  return rows;
};

const processItem = ({
  item,
  isFromOtherStation,
  formatCurrency,
  storage,
  i18n,
  styling,
  isLastItemInGroup = false,
  isAppOrder = false,
}: {
  item: LocalItem & { count: number };
  isFromOtherStation: boolean;
  formatCurrency: (amount: number) => string;
  storage: StorageData;
  i18n: (key: string) => string;
  styling: Record<string, any>;
  isLastItemInGroup?: boolean;
  isAppOrder?: boolean;
}): PrintRow[] => {
  const rows: PrintRow[] = [];

  let title = item.title;

  if (!storage.BongDisableCapitalLetters && !isFromOtherStation) {
    title = title.toUpperCase();
  }

  let itemContent = isFromOtherStation ? `${item.count}x ${title}` : `➤ ${item.count}x ${title}`; // Add the little arrow for main items.

  // Add price if enabled and available
  if (
    !isAppOrder &&
    styling.ITEM_TITLE?.showPrice &&
    item.amount !== undefined &&
    !isFromOtherStation
  ) {
    const price = formatCurrency(item.amount - (item.discount || 0));
    itemContent += ` ${price}`;
  }

  const itemStyling = isFromOtherStation ? styling.ITEM_FROM_OTHER_STATION : styling.ITEM_TITLE;
  const spacing = isFromOtherStation ? 1 : 0;

  rows.push({
    content: `${' '.repeat(spacing)}${itemContent}`,
    fontSize: itemStyling?.fontSize || DEFAULT_TICKET_FONT_SIZE,
    bold: itemStyling?.fontWeight === 'bold',
    alignment: mapAlignment(itemStyling?.textAlign),
    doubleHeigth: (itemStyling?.fontSize || DEFAULT_TICKET_FONT_SIZE) >= 30,
  });

  // If item is from another station and showDetails is false, skip variations and comments
  if (isFromOtherStation && styling.ITEM_FROM_OTHER_STATION?.showDetails === false) {
    return rows;
  }

  const hasDetails =
    item.variations.length > 0 || item.removedVariations.length > 0 || item.message;

  if (!hasDetails) {
    return rows;
  }

  // Process variations
  item.variations.forEach((variation) => {
    const prefix = styling.VARIATION?.prefix || '';
    let variationContent = `${prefix}${variation.name}`;

    if (
      !isAppOrder &&
      styling.VARIATION?.showPrice &&
      variation.price !== undefined &&
      !isFromOtherStation
    ) {
      variationContent += ` ${formatCurrency(variation.price)}`;
    }

    const variationStyling = isFromOtherStation
      ? styling.ITEM_FROM_OTHER_STATION
      : styling.VARIATION;
    rows.push({
      content: `${' '.repeat(6)}${variationContent}`,
      fontSize: variationStyling?.fontSize || DEFAULT_TICKET_FONT_SIZE,
      bold: variationStyling?.fontWeight === 'bold',
      alignment: mapAlignment(variationStyling?.textAlign),
    });
  });

  // Process removed variations
  item.removedVariations.forEach((variation) => {
    const prefix = styling.REMOVED_VARIATION?.prefix || `${i18n('Bong.Remove')}: `;
    const removedStyling = isFromOtherStation
      ? styling.ITEM_FROM_OTHER_STATION
      : styling.REMOVED_VARIATION;

    rows.push({
      content: `${' '.repeat(6)}${prefix}${variation.name}`,
      fontSize: removedStyling?.fontSize || DEFAULT_TICKET_FONT_SIZE,
      bold: removedStyling?.fontWeight === 'bold',
      alignment: mapAlignment(removedStyling?.textAlign),
    });
  });

  // Process item message/comment
  if (item.message) {
    const commentStyling = isFromOtherStation
      ? styling.ITEM_FROM_OTHER_STATION
      : styling.ITEM_COMMENT;
    rows.push({
      content: `${' '.repeat(8)}${item.message}`,
      fontSize: commentStyling?.fontSize || DEFAULT_TICKET_FONT_SIZE,
      bold: commentStyling?.fontWeight === 'bold',
      alignment: mapAlignment(commentStyling?.textAlign),
    });
  }

  // If the item is not the last in the group we want some extra spacing under item details(variations/comments)
  if (!isLastItemInGroup) {
    rows.push(...createSpacing());
  }

  return rows;
};
